/**
 * Lightweight client-side structured logger.
 *
 * Routes each level to the matching `console` method and prepends an optional
 * scope. The active minimum level is controlled by `VITE_LOG_LEVEL`
 * (debug|info|warn|error); it falls back to `debug` in development and `info`
 * in production builds, so noisy debug output never reaches end users.
 */

export type LogLevel = "debug" | "info" | "warn" | "error";

const LEVEL_WEIGHT: Record<LogLevel, number> = {
	debug: 10,
	info: 20,
	warn: 30,
	error: 40,
};

type EnvSource = Record<string, unknown>;

const resolveThreshold = (env: EnvSource = import.meta.env): number => {
	const configured = String(env.VITE_LOG_LEVEL ?? "").trim().toLowerCase();
	if (configured && configured in LEVEL_WEIGHT) {
		return LEVEL_WEIGHT[configured as LogLevel];
	}

	return env.DEV ? LEVEL_WEIGHT.debug : LEVEL_WEIGHT.info;
};

export interface Logger {
	debug(message: string, context?: Record<string, unknown>): void;
	info(message: string, context?: Record<string, unknown>): void;
	warn(message: string, context?: Record<string, unknown>): void;
	error(message: string, context?: Record<string, unknown>): void;
}

const consoleFor: Record<LogLevel, (...args: unknown[]) => void> = {
	debug: (...args) => console.debug(...args),
	info: (...args) => console.info(...args),
	warn: (...args) => console.warn(...args),
	error: (...args) => console.error(...args),
};

const emit = (
	scope: string | undefined,
	level: LogLevel,
	message: string,
	context?: Record<string, unknown>
): void => {
	if (LEVEL_WEIGHT[level] < resolveThreshold()) {
		return;
	}

	const prefix = scope ? `[${scope}] ${message}` : message;
	if (context === undefined) {
		consoleFor[level](prefix);
	} else {
		consoleFor[level](prefix, context);
	}
};

/**
 * Creates a structured client logger. Pass a `scope` (e.g. a module name) to
 * prefix every record with its origin.
 */
export const createLogger = (scope?: string): Logger => ({
	debug: (message, context) => emit(scope, "debug", message, context),
	info: (message, context) => emit(scope, "info", message, context),
	warn: (message, context) => emit(scope, "warn", message, context),
	error: (message, context) => emit(scope, "error", message, context),
});

export const logger = createLogger();
